// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/contabil/components/AcessoContadorAba.tsx
//  Aba "Acesso do contador" da Plataforma Contador: o cliente libera o Portal do
//  Contador para o escritório (escolhendo as empresas), vê quem tem acesso e revoga.
//  Liberar é o consentimento: o contador passa a ver XMLs e análises dessas empresas.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Loader2, ShieldCheck, UserPlus, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { acessoContadorService as svc, type AcessoContador } from '../services/acessoContadorService';

interface Props {
  podeAlterar: boolean;
  /** Contador já cadastrado na empresa (Cadastro da empresa): sugestão para o convite. */
  sugestao?: { email?: string | null; nome?: string | null; cnpj?: string | null };
}

export function AcessoContadorAba({ podeAlterar, sugestao }: Props) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['acesso-contador'], queryFn: svc.listar });
  const [email, setEmail] = useState(sugestao?.email ?? '');
  const [nome, setNome] = useState(sugestao?.nome ?? '');
  const [cnpj, setCnpj] = useState(sugestao?.cnpj ?? '');
  const [empresas, setEmpresas] = useState<number[] | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [revogar, setRevogar] = useState<AcessoContador | null>(null);

  // Todas as empresas marcadas até o usuário mexer.
  const marcadas = empresas ?? data?.empresas.map((e) => e.id) ?? [];

  const convidar = useMutation({
    mutationFn: () => svc.convidar({ email, nome, cnpjEscritorio: cnpj, razaoEscritorio: nome, empresaIds: marcadas }),
    onSuccess: (r) => {
      setLink(r.emailEnviado ? null : r.linkConvite ?? null);
      toast.success('Acesso liberado', { description: r.mensagem });
      qc.invalidateQueries({ queryKey: ['acesso-contador'] });
    },
    onError: (e) => toast.error('Não foi possível liberar', { description: getApiErrorMessage(e) }),
  });

  const revogacao = useMutation({
    mutationFn: (id: number) => svc.revogar(id),
    onSuccess: () => {
      toast.success('Acesso revogado', { description: 'O contador deixou de ver os documentos desta empresa.' });
      qc.invalidateQueries({ queryKey: ['acesso-contador'] });
    },
    onError: (e) => toast.error('Não foi possível revogar', { description: getApiErrorMessage(e) }),
    onSettled: () => setRevogar(null),
  });

  const alternar = (id: number, marcar: boolean) =>
    setEmpresas(marcar ? [...marcadas, id] : marcadas.filter((x) => x !== id));

  return (
    <div className="space-y-6">
      <Alert>
        <ShieldCheck className="h-4 w-4" />
        <AlertDescription>
          O <b>Portal do Contador</b> dá ao seu escritório de contabilidade acesso direto aos XMLs e à conferência fiscal
          das empresas que você liberar, sem precisar mandar arquivos por e-mail. Você pode revogar a qualquer momento.
        </AlertDescription>
      </Alert>

      {podeAlterar && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Liberar acesso para o contador</CardTitle>
            <CardDescription>O contador recebe um link para criar a senha e entra em /contador.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="ac-email">E-mail do contador *</Label>
                <Input id="ac-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ac-nome">Nome do contador ou escritório</Label>
                <Input id="ac-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ac-cnpj">CNPJ/CPF do escritório</Label>
                <Input id="ac-cnpj" value={cnpj} onChange={(e) => setCnpj(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Empresas que o contador poderá ver</Label>
              {isLoading ? <Skeleton className="h-6 w-64" /> : data?.empresas.length ? (
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {data.empresas.map((e) => (
                    <label key={e.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={marcadas.includes(e.id)} onCheckedChange={(v) => alternar(e.id, v === true)} />
                      {e.razaoSocial ?? e.cnpjCpf}
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhuma empresa com CNPJ no cadastro entre as suas.</p>
              )}
            </div>
            <Button onClick={() => convidar.mutate()} disabled={convidar.isPending || !email.trim() || marcadas.length === 0} className="gap-2">
              {convidar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Liberar acesso
            </Button>
            {link && (
              <Alert>
                <AlertDescription className="space-y-2">
                  <p>O e-mail não pôde ser enviado. Copie o link e mande ao contador (vale 7 dias):</p>
                  <div className="flex gap-2">
                    <Input readOnly value={link} className="font-mono text-xs" />
                    <Button variant="outline" size="icon" title="Copiar link"
                      onClick={() => navigator.clipboard.writeText(link).then(() => toast.success('Link copiado'))}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Quem tem acesso</CardTitle></CardHeader>
        <CardContent className="p-0">
          {isLoading ? <div className="p-4"><Skeleton className="h-16" /></div> : !data?.acessos.length ? (
            <p className="px-6 pb-6 text-sm text-muted-foreground">Nenhum contador com acesso ainda.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Escritório</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Usuários</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.acessos.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.escritorio}</TableCell>
                    <TableCell>{a.empresa ?? '—'}</TableCell>
                    <TableCell className="text-sm">
                      {a.usuarios.map((u) => (
                        <div key={u.email}>
                          {u.email}{' '}
                          {!u.senhaDefinida && <span className="text-xs text-muted-foreground">(ainda não aceitou o convite)</span>}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>
                      {a.ativo
                        ? <Badge variant="outline">Liberado em {new Date(a.dtAutorizacao).toLocaleDateString('pt-BR')}</Badge>
                        : <Badge variant="secondary">Revogado{a.dtRevogacao ? ` em ${new Date(a.dtRevogacao).toLocaleDateString('pt-BR')}` : ''}</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      {a.ativo && podeAlterar && (
                        <Button variant="ghost" size="sm" className="gap-1 text-destructive" onClick={() => setRevogar(a)}>
                          <XCircle className="h-4 w-4" />Revogar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={revogar !== null} onOpenChange={(aberto) => !aberto && setRevogar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar o acesso?</AlertDialogTitle>
            <AlertDialogDescription>
              {revogar?.escritorio} deixa de ver os documentos de {revogar?.empresa} imediatamente. Você pode liberar de novo depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => revogar && revogacao.mutate(revogar.id)}>Revogar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
